/**
 * resolver/resolver.js - Style resolution engine
 * Location: /app/frontend/src/admin/page_builder/resolver/
 * 
 * Resolves styles from multiple layers with proper precedence:
 * elementOverride > blockOverride > pageOverride > globalTokens > componentDefaults
 * 
 * Outputs TWO maps:
 * - cssStyles: token refs become var(--tc-<path>)
 * - resolvedStyles: token refs resolved to actual values
 * Plus metadata with sources and isTokenRef flags.
 */

import { isTokenRef, getTokenPath, tokenPathToCSSVar, getTokenValue, defaultTokens } from '../tokens/config';
import { StyleSource, createStyleEntry, createEmptyOutput } from './types';
import { mergeDeltaLayers } from './merge';

/**
 * StyleResolver class - resolves styles for a single element
 * 
 * Usage:
 * const resolver = new StyleResolver(globalTokens);
 * const output = resolver.resolve({
 *   componentDefaults: { color: 'white', fontSize: '16px' },
 *   pageOverrides: { color: { $token: 'colors.primary' } },
 *   blockOverrides: {},
 *   elementOverrides: { fontSize: '18px' }
 * });
 */
export class StyleResolver {
  /**
   * @param {Object} globalTokens - The global token values (from API or defaults)
   */
  constructor(globalTokens = defaultTokens) {
    this.tokens = globalTokens;
  }
  
  /**
   * Update the global tokens
   * @param {Object} tokens - New token values
   */
  setTokens(tokens) {
    this.tokens = tokens;
  }
  
  /**
   * Resolve a value that might be a token reference
   * @param {*} value - Value to resolve
   * @returns {{ cssValue: string, resolvedValue: string, isToken: boolean, tokenPath: string|null }}
   */
  resolveValue(value) {
    if (isTokenRef(value)) {
      const tokenPath = getTokenPath(value);
      const resolvedValue = getTokenValue(tokenPath, this.tokens);
      const cssVar = tokenPathToCSSVar(tokenPath);
      
      return {
        cssValue: `var(${cssVar})`,
        resolvedValue: resolvedValue !== undefined ? String(resolvedValue) : 'undefined',
        isToken: true,
        tokenPath
      };
    }
    
    // Plain value - use as-is
    const strValue = value !== undefined && value !== null ? String(value) : '';
    return {
      cssValue: strValue,
      resolvedValue: strValue,
      isToken: false,
      tokenPath: null
    };
  }
  
  /**
   * Resolve styles from all layers with proper precedence
   * 
   * @param {Object} layers - Style layers
   * @param {Object} [layers.componentDefaults={}] - Component default styles
   * @param {Object} [layers.pageOverrides={}] - Page-level style overrides
   * @param {Object} [layers.blockOverrides={}] - Block-level style overrides
   * @param {Object} [layers.elementOverrides={}] - Element-level style overrides
   * @returns {StyleResolverOutput} Resolved styles with dual output + metadata
   */
  resolve({ componentDefaults = {}, pageOverrides = {}, blockOverrides = {}, elementOverrides = {} } = {}) {
    // Build layer array in precedence order (lowest first)
    const layers = [
      [componentDefaults, StyleSource.COMPONENT_DEFAULT],
      [pageOverrides, StyleSource.PAGE_OVERRIDE],
      [blockOverrides, StyleSource.BLOCK_OVERRIDE],
      [elementOverrides, StyleSource.ELEMENT_OVERRIDE]
    ];
    
    // Merge all layers with source tracking
    const merged = mergeDeltaLayers(layers);
    
    // Build output
    const output = createEmptyOutput();
    
    for (const [property, { value, source }] of Object.entries(merged)) {
      const resolved = this.resolveValue(value);
      
      output.cssStyles[property] = resolved.cssValue;
      output.resolvedStyles[property] = resolved.resolvedValue;
      output.metadata[property] = createStyleEntry(
        resolved.resolvedValue,
        source,
        resolved.isToken,
        resolved.tokenPath
      );
    }
    
    return output;
  }
  
  /**
   * Quick resolve for a single property (useful for previews)
   * @param {*} value - Property value (may be token ref)
   * @param {number} source - StyleSource enum value
   * @returns {ResolvedStyleEntry}
   */
  resolveProperty(value, source = StyleSource.COMPONENT_DEFAULT) {
    const resolved = this.resolveValue(value);
    return createStyleEntry(
      resolved.resolvedValue,
      source,
      resolved.isToken,
      resolved.tokenPath
    );
  }
}

/**
 * Create a resolver instance with the given tokens
 * @param {Object} tokens - Global tokens
 * @returns {StyleResolver}
 */
export function createResolver(tokens = defaultTokens) {
  return new StyleResolver(tokens);
}

/**
 * Quick resolve function without creating a resolver instance
 * Useful for one-off resolutions
 * 
 * @param {Object} layers - Style layers
 * @param {Object} tokens - Global tokens
 * @returns {StyleResolverOutput}
 */
export function resolveStyles(layers, tokens = defaultTokens) {
  const resolver = new StyleResolver(tokens);
  return resolver.resolve(layers);
}

/**
 * Resolve a single value (convenience function)
 * @param {*} value - Value to resolve
 * @param {Object} tokens - Global tokens
 * @returns {{ cssValue: string, resolvedValue: string, isToken: boolean, tokenPath: string|null }}
 */
export function resolveValue(value, tokens = defaultTokens) {
  const resolver = new StyleResolver(tokens);
  return resolver.resolveValue(value);
}

export default {
  StyleResolver,
  createResolver,
  resolveStyles,
  resolveValue
};
